import {
  Badge,
  buttonVariants,
  cn,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  ExternalLink,
  Plus,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useCreateManualOrder,
  useCreateManualOrders,
  useFeedCommentsQuery,
  useFeedCustomersQuery,
  useFeedDetailQuery,
  useFeedPaidProductSalesQuery,
  useFeedProductMonitorQuery,
  useFeedProductSalesQuery,
  useFeedSessionProductsDetailQuery,
  useFeedSessionProductsInfiniteQuery,
  usePatchFeedSessionProduct,
  useRemoveSessionProduct,
} from "@/modules/cf-feed/presentation/api/queries";
import type { FeedCommentRow } from "@/modules/cf-feed/presentation/api/types";
import { FeedCommentOrderGuide } from "@/modules/cf-feed/presentation/ui/FeedCommentOrderGuide";
import { FeedCommentsTable } from "@/modules/cf-feed/presentation/ui/FeedCommentsTable";
import { FeedCustomersTable } from "@/modules/cf-feed/presentation/ui/FeedCustomersTable";
import { FeedProductMonitorTable } from "@/modules/cf-feed/presentation/ui/FeedProductMonitorTable";
import {
  FeedProductsTable,
  sessionProductOptionSignature,
} from "@/modules/cf-feed/presentation/ui/FeedProductsTable";
import { FeedSalesTable } from "@/modules/cf-feed/presentation/ui/FeedSalesTable";
import { FeedWorkspaceNavigation } from "@/modules/cf-feed/presentation/ui/FeedWorkspaceNavigation";
import { LinkMessengerProfileDialog } from "@/modules/cf-feed/presentation/ui/LinkMessengerProfileDialog";
import { ManualOrderDialog } from "@/modules/cf-feed/presentation/ui/ManualOrderDialog";
import { SessionProductOptionsDialog } from "@/modules/cf-feed/presentation/ui/SessionProductOptionsDialog";
import { SessionPromoPanel } from "@/modules/cf-feed/presentation/ui/SessionPromoPanel";
import { SpecialWorkDialog } from "@/modules/cf-feed/presentation/ui/SpecialWorkDialog";
import { formatKip } from "@/modules/cf-feed/presentation/ui/table-helpers";
import { useWorkspaceEnabled } from "@/modules/cf-live/presentation/api/workspace-client";
import { LiveWorkspacePage } from "@/modules/cf-live/presentation/pages/LiveWorkspacePage";
import { getManualOrderErrorReason } from "@/shared/lib/cf-api-error-messages";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { AppImage } from "@/shared/ui/AppImage";
import { QueryState } from "@/shared/ui/QueryState";
import { useFeedProductMonitorDisplaySelection } from "../lib/feed-product-monitor-display-selection";
import { useFeedProductMonitorVisualHighlight } from "../lib/feed-product-monitor-visual-highlight";

const FEED_DETAIL_TABS = [
  "customers",
  "paid",
  "sales",
  "monitor",
  "products",
  "comments",
  "guide",
] as const;
type FeedDetailTab = (typeof FEED_DETAIL_TABS)[number];

function resolveFeedDetailTab(value: unknown): FeedDetailTab {
  return FEED_DETAIL_TABS.includes(value as FeedDetailTab)
    ? (value as FeedDetailTab)
    : "customers";
}

export function FeedDetailPage() {
  const { id } = useParams({ from: "/app/feeds/$id" });
  const search = useSearch({ from: "/app/feeds/$id" });
  const capability = useWorkspaceEnabled();
  if (capability.isLoading) return <p className="p-6">ກຳລັງໂຫຼດ…</p>;
  if (
    (search.tab === "products" || search.tab === "monitor") &&
    capability.data?.enabled
  )
    return <LiveWorkspacePage kind="feed" id={id} />;
  return <LegacyFeedDetailPage />;
}

function LegacyFeedDetailPage() {
  const workspace = useWorkspaceEnabled();
  const { id: feedId } = useParams({ from: "/app/feeds/$id" });
  const search = useSearch({ from: "/app/feeds/$id" }) as {
    tab?: string;
  };
  const nav = useNavigate({ from: "/app/feeds/$id" });
  const activeTab = resolveFeedDetailTab(search.tab);
  const summaryQuery = useFeedDetailQuery(feedId);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [commentSearchInput, setCommentSearchInput] = useState("");
  const [manualProductSearchInput, setManualProductSearchInput] = useState("");
  const [customerQ, setCustomerQ] = useState("");
  const [commentQ, setCommentQ] = useState("");
  const [manualProductQ, setManualProductQ] = useState("");
  const [manualProductsPage, setManualProductsPage] = useState({
    offset: 0,
    limit: 10,
  });
  const [selectedCommentIds, setSelectedCommentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [manualOrderTarget, setManualOrderTarget] = useState<{
    commentIds: string[];
    comments: FeedCommentRow[];
  } | null>(null);
  const [manualOrderError, setManualOrderError] = useState<string | null>(null);
  const [messengerLinkTarget, setMessengerLinkTarget] =
    useState<FeedCommentRow | null>(null);
  const debouncedCustomerSearch = useDebounceCallback((value: string) => {
    setCustomerQ(value.trim());
  }, 350);
  const debouncedCommentSearch = useDebounceCallback((value: string) => {
    setCommentQ(value.trim());
  }, 350);
  const debouncedManualProductSearch = useDebounceCallback((value: string) => {
    setManualProductQ(value.trim());
    setManualProductsPage((page) => ({ ...page, offset: 0 }));
  }, 350);
  const customersQuery = useFeedCustomersQuery(
    feedId,
    customerQ || undefined,
    activeTab === "customers",
  );
  const salesQuery = useFeedProductSalesQuery(feedId, activeTab === "sales");
  const salesProducts = useMemo(
    () => salesQuery.data?.pages.flatMap((page) => page.products) ?? [],
    [salesQuery.data],
  );
  const paidSalesQuery = useFeedPaidProductSalesQuery(
    feedId,
    activeTab === "paid",
  );
  const paidSalesProducts = useMemo(
    () => paidSalesQuery.data?.pages.flatMap((page) => page.products) ?? [],
    [paidSalesQuery.data],
  );
  const monitorQuery = useFeedProductMonitorQuery(
    feedId,
    activeTab === "monitor",
  );
  const monitorProducts = useMemo(
    () => monitorQuery.data?.pages.flatMap((page) => page.products) ?? [],
    [monitorQuery.data],
  );
  const monitorTotals = monitorQuery.data?.pages[0]?.totals ?? null;
  const {
    displayCount,
    maxDisplayProducts,
    isProductOnDisplay,
    getProductDisplayPosition,
    toggleProductDisplay,
  } = useFeedProductMonitorDisplaySelection(feedId);
  const {
    isProductHighlighted,
    isVariantHighlighted,
    toggleProductHighlight,
    toggleVariantHighlight,
  } = useFeedProductMonitorVisualHighlight(feedId);

  const handleToggleProductDisplay = (productId: string) => {
    const result = toggleProductDisplay(productId);
    if (result.replacedProductId) {
      toast.info(
        `ຄິວໜ້າຈໍເຕັມ ${maxDisplayProducts} ລາຍການ — ນຳລາຍການໃໝ່ແທນຄິວເກົ່າສຸດແລ້ວ`,
      );
    }
  };
  const productsQuery = useFeedSessionProductsInfiniteQuery(
    feedId,
    activeTab === "products",
  );
  const manualProductsQuery = useFeedSessionProductsDetailQuery(
    feedId,
    {
      ...manualProductsPage,
      saleEnabled: true,
      q: manualProductQ || undefined,
    },
    Boolean(manualOrderTarget),
  );
  const commentsQuery = useFeedCommentsQuery(
    feedId,
    { q: commentQ || undefined },
    activeTab === "comments",
  );
  const customerRows = useMemo(
    () => customersQuery.data?.pages.flatMap((page) => page.customers) ?? [],
    [customersQuery.data],
  );
  const commentRows = useMemo(
    () => commentsQuery.data?.pages.flatMap((page) => page.comments) ?? [],
    [commentsQuery.data],
  );
  const messengerIdentityPendingTotal =
    commentsQuery.data?.pages[0]?.messengerIdentityPendingTotal ?? 0;
  const productGroups = useMemo(
    () =>
      productsQuery.data?.pages.flatMap((page) => page.products ?? []) ?? [],
    [productsQuery.data],
  );
  const productItems = useMemo(
    () => productGroups.flatMap((group) => group.variants),
    [productGroups],
  );
  const patchProduct = usePatchFeedSessionProduct(feedId);
  const removeProduct = useRemoveSessionProduct(feedId);
  const createManualOrder = useCreateManualOrder(feedId);
  const createOrders = useCreateManualOrders(feedId);
  const [optionsTarget, setOptionsTarget] = useState<{
    ids: string[];
    title: string;
    discountPercent: number | null;
    discountFixedLak: number | null;
  } | null>(null);
  const [specialWorkOrderId, setSpecialWorkOrderId] = useState<string | null>(
    null,
  );
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    () => new Set(),
  );
  // ล้าง selection ของ session product ที่หาย เพื่อกัน state ค้าง
  useEffect(() => {
    setSelectedProductIds((prev) => {
      const ids = new Set(productItems.map((p) => p.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (ids.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [productItems]);

  const data = summaryQuery.data;
  const feed = data?.feed;
  const stats = data?.stats;

  return (
    <>
      <Header />
      <Main className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <nav className="flex items-center gap-1 text-muted-foreground text-sm">
              <Link to="/app/feeds" className="hover:text-foreground">
                ຈັດການໂພສ
              </Link>
              <ChevronRight className="size-4 shrink-0" />
              <span className="text-foreground">ລາຍລະອຽດງານໂພສ</span>
            </nav>
            <Link
              to="/app/feeds"
              className={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "-ml-2 w-fit sm:ml-0",
              )}
            >
              <ArrowLeft className="size-4" />
              ກັບລາຍການໂພສ
            </Link>
          </div>

          {workspace.data?.enabled && (
            <FeedWorkspaceNavigation feedId={feedId} activeTab={activeTab} />
          )}
          <QueryState result={summaryQuery}>
            {feed && stats ? (
              <>
                <section className="space-y-8">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1 space-y-3">
                      <h1 className="font-semibold text-2xl tracking-tight">
                        {feed.messagePreview ?? feed.title ?? "ໂພສ Facebook"}
                      </h1>
                      <p className="text-muted-foreground text-sm">
                        {feed.pageName}
                        {feed.publishedAt
                          ? ` · ${formatDateTimeLocal(feed.publishedAt)}`
                          : null}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge>{feed.displayStatusLabel}</Badge>
                      </div>
                    </div>
                    {feed.permalinkUrl ? (
                      <a
                        href={feed.permalinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={buttonVariants({
                          variant: "outline",
                          size: "sm",
                        })}
                      >
                        <ExternalLink className="size-4" />
                        Facebook
                      </a>
                    ) : null}
                  </div>

                  <details>
                    <summary className="min-h-11 cursor-pointer py-3 text-sm">
                      ລາຍລະອຽດໂພສ Facebook
                    </summary>
                    <div className="grid gap-6 md:grid-cols-[220px_minmax(0,1fr)] md:gap-8">
                      <AppImage
                        src={feed.thumbnailUrl}
                        alt={feed.title ?? "Facebook post"}
                        className="aspect-video w-full rounded-xl border bg-muted"
                        fit="cover"
                      />
                      <div className="min-w-0 space-y-5">
                        <div className="space-y-2">
                          <p className="text-muted-foreground text-xs">
                            ເນື້ອຫາໂພສ
                          </p>
                          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                            {feed.messagePreview ?? feed.title ?? "-"}
                          </p>
                        </div>
                        <div className="grid gap-4 text-sm sm:grid-cols-2">
                          <PostMeta
                            label="Facebook Feed ID"
                            value={feed.facebookFeedId}
                          />
                          <PostMeta
                            label="Feed status"
                            value={feed.feedStatus}
                          />
                          <PostMeta
                            label="ຄອມເມັນຈາກ Facebook"
                            value={
                              feed.commentCount == null
                                ? "-"
                                : String(feed.commentCount)
                            }
                          />
                          <PostMeta
                            label="ອັບເດດຈາກ Facebook"
                            value={
                              feed.graphUpdatedAt
                                ? formatDateTimeLocal(feed.graphUpdatedAt)
                                : "-"
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </details>
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-5">
                    <StatCard
                      label="ຈຳນວນ CF ທັງໝົດ"
                      value={`${stats.claimQuantity} ຊິ້ນ`}
                    />
                    <StatCard
                      label="ມູນຄ່າ CF ທັງໝົດ"
                      value={formatKip(stats.claimValue)}
                    />
                    <StatCard
                      label="ຈຳນວນຂາຍຈິງ"
                      value={`${stats.actualQuantity} ຊິ້ນ`}
                    />
                    <StatCard
                      label="ຍອດຂາຍຈິງ"
                      value={formatKip(stats.revenue)}
                    />
                  </div>
                </section>

                {!workspace.data?.enabled && (
                  <SessionPromoPanel feedId={feedId} />
                )}

                <Tabs
                  value={activeTab}
                  onValueChange={(value) =>
                    nav({
                      search: (prev) => ({
                        ...prev,
                        tab: resolveFeedDetailTab(value),
                      }),
                      replace: true,
                      resetScroll: false,
                    })
                  }
                  className="space-y-6"
                >
                  {!workspace.data?.enabled && (
                    <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
                      <TabsTrigger value="customers">ລູກຄ້າ</TabsTrigger>
                      <TabsTrigger value="monitor">ຕິດຕາມສິນຄ້າ</TabsTrigger>
                      <TabsTrigger value="paid">ຍອດຂາຍຈິງ</TabsTrigger>
                      <TabsTrigger value="sales">CF ທັງໝົດ</TabsTrigger>
                      <TabsTrigger value="products">ສິນຄ້າ</TabsTrigger>
                      <TabsTrigger value="comments">ຄອມເມັນທັງໝົດ</TabsTrigger>
                      <TabsTrigger value="guide">ວິທີຄອມເມັນ</TabsTrigger>
                    </TabsList>
                  )}

                  <TabsContent value="customers">
                    <TabPanel
                      title="ລູກຄ້າ"
                      search={
                        <Input
                          placeholder="ຄົ້ນຫາຕາມຊື່ລູກຄ້າ..."
                          value={customerSearchInput}
                          onChange={(e) => {
                            setCustomerSearchInput(e.target.value);
                            debouncedCustomerSearch(e.target.value);
                          }}
                          className="max-w-sm"
                        />
                      }
                    >
                      <QueryState
                        isLoading={customersQuery.isLoading}
                        isError={customersQuery.isError}
                        error={customersQuery.error}
                        onRetry={() => void customersQuery.refetch()}
                        isFetching={
                          customersQuery.isFetching &&
                          !customersQuery.isFetchingNextPage
                        }
                      >
                        <FeedCustomersTable
                          feedId={feedId}
                          data={customerRows}
                          total={customersQuery.data?.pages[0]?.total ?? 0}
                          isLoading={customersQuery.isLoading}
                          hasNextPage={Boolean(customersQuery.hasNextPage)}
                          isFetchingNextPage={customersQuery.isFetchingNextPage}
                          onLoadMore={() => {
                            if (
                              customersQuery.hasNextPage &&
                              !customersQuery.isFetchingNextPage
                            ) {
                              void customersQuery.fetchNextPage();
                            }
                          }}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="paid">
                    <TabPanel
                      title="ຍອດຂາຍຈິງ"
                      description="ສະເພາະອໍເດີ້ທີ່ຈ່າຍແລ້ວ ແລະບໍ່ຖືກຍົກເລີກ"
                    >
                      <QueryState
                        isLoading={paidSalesQuery.isLoading}
                        isError={paidSalesQuery.isError}
                        error={paidSalesQuery.error}
                        onRetry={() => void paidSalesQuery.refetch()}
                        isFetching={
                          paidSalesQuery.isFetching &&
                          !paidSalesQuery.isFetchingNextPage
                        }
                      >
                        <FeedSalesTable
                          products={paidSalesProducts}
                          mode="actual"
                          isLoading={paidSalesQuery.isLoading}
                          hasNextPage={Boolean(paidSalesQuery.hasNextPage)}
                          isFetchingNextPage={paidSalesQuery.isFetchingNextPage}
                          onLoadMore={() => {
                            if (
                              paidSalesQuery.hasNextPage &&
                              !paidSalesQuery.isFetchingNextPage
                            ) {
                              void paidSalesQuery.fetchNextPage();
                            }
                          }}
                          onEditSpecialWork={setSpecialWorkOrderId}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="sales">
                    <TabPanel
                      title="CF ທັງໝົດ"
                      description="ນັບທຸກອໍເດີ້ທີ່ເກີດຈາກ CF ລວມທັງລໍຖ້າ ແລະຍົກເລີກ"
                    >
                      <QueryState
                        isLoading={salesQuery.isLoading}
                        isError={salesQuery.isError}
                        error={salesQuery.error}
                        onRetry={() => void salesQuery.refetch()}
                        isFetching={
                          salesQuery.isFetching &&
                          !salesQuery.isFetchingNextPage
                        }
                      >
                        <FeedSalesTable
                          products={salesProducts}
                          mode="cf"
                          isLoading={salesQuery.isLoading}
                          hasNextPage={Boolean(salesQuery.hasNextPage)}
                          isFetchingNextPage={salesQuery.isFetchingNextPage}
                          onLoadMore={() => {
                            if (
                              salesQuery.hasNextPage &&
                              !salesQuery.isFetchingNextPage
                            ) {
                              void salesQuery.fetchNextPage();
                            }
                          }}
                          onEditSpecialWork={setSpecialWorkOrderId}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="monitor">
                    <TabPanel
                      title="ຕິດຕາມສິນຄ້າ"
                      action={
                        <button
                          type="button"
                          className={buttonVariants({
                            variant: "outline",
                            size: "sm",
                          })}
                          onClick={() => {
                            window.open(
                              `/app/feeds/${encodeURIComponent(feedId)}/monitor-display`,
                              `cf-feed-monitor-${feedId}`,
                              "noopener,noreferrer",
                            );
                          }}
                        >
                          <ExternalLink className="size-4" />
                          ເປີດໜ້າຈໍໃໝ່
                        </button>
                      }
                    >
                      <QueryState
                        isLoading={monitorQuery.isLoading}
                        isError={monitorQuery.isError}
                        error={monitorQuery.error}
                        onRetry={() => void monitorQuery.refetch()}
                        isFetching={
                          monitorQuery.isFetching &&
                          !monitorQuery.isFetchingNextPage
                        }
                      >
                        <FeedProductMonitorTable
                          products={monitorProducts}
                          totals={monitorTotals}
                          isLoading={monitorQuery.isLoading}
                          hasNextPage={Boolean(monitorQuery.hasNextPage)}
                          isFetchingNextPage={monitorQuery.isFetchingNextPage}
                          defaultExpandProducts={false}
                          isProductOnDisplay={isProductOnDisplay}
                          getProductDisplayPosition={getProductDisplayPosition}
                          onToggleProductDisplay={handleToggleProductDisplay}
                          displayCount={displayCount}
                          maxDisplayProducts={maxDisplayProducts}
                          displayToggleLabels={{
                            on: "ຢູ່ໜ້າຈໍ",
                            off: "ເພີ່ມຂຶ້ນໜ້າຈໍ",
                          }}
                          isProductHighlighted={isProductHighlighted}
                          isVariantHighlighted={isVariantHighlighted}
                          onToggleProductHighlight={toggleProductHighlight}
                          onToggleVariantHighlight={toggleVariantHighlight}
                          emphasisToggleLabels={{
                            on: "ຍົກເລີກ",
                            off: "ເນັ້ນໃຫ້ເດັ່ນ",
                          }}
                          onLoadMore={() => {
                            if (
                              monitorQuery.hasNextPage &&
                              !monitorQuery.isFetchingNextPage
                            ) {
                              void monitorQuery.fetchNextPage();
                            }
                          }}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="products">
                    <TabPanel
                      title="ສິນຄ້າ"
                      action={
                        <Link
                          to="/app/feeds/post-products"
                          search={{ pageId: feed.pageId, feedId }}
                          className={buttonVariants({ size: "sm" })}
                        >
                          <Plus className="size-4" />
                          ເພີ່ມສິນຄ້າ
                        </Link>
                      }
                    >
                      <QueryState
                        isLoading={productsQuery.isLoading}
                        isError={productsQuery.isError}
                        error={productsQuery.error}
                        onRetry={() => void productsQuery.refetch()}
                        isFetching={
                          productsQuery.isFetching &&
                          !productsQuery.isFetchingNextPage
                        }
                      >
                        <FeedProductsTable
                          products={productGroups}
                          isLoading={productsQuery.isLoading}
                          hasNextPage={Boolean(productsQuery.hasNextPage)}
                          isFetchingNextPage={productsQuery.isFetchingNextPage}
                          onLoadMore={() => {
                            if (
                              productsQuery.hasNextPage &&
                              !productsQuery.isFetchingNextPage
                            ) {
                              void productsQuery.fetchNextPage();
                            }
                          }}
                          selectedIds={selectedProductIds}
                          onSelectionChange={setSelectedProductIds}
                          onBulkEditOptions={(ids) => {
                            const rows = productItems.filter((p) =>
                              ids.includes(p.id),
                            );
                            if (rows.length < 2) return;
                            const signatures = new Set(
                              rows.map((r) =>
                                sessionProductOptionSignature(r.options ?? []),
                              ),
                            );
                            if (signatures.size > 1) {
                              toast.error("ເລືອກສິນຄ້າທີ່ມີ option ຊຸດຄືກັນເທົ່ານັ້ນ");
                              return;
                            }
                            const first = rows[0];
                            if (!first) return;
                            setOptionsTarget({
                              ids,
                              title: `${ids.length} ລາຍການ (option ຄືກັນ)`,
                              discountPercent: first.discountPercent,
                              discountFixedLak: first.discountFixedLak,
                            });
                          }}
                          onToggleSale={(id, enabled) =>
                            patchProduct.mutate({
                              spId: id,
                              saleEnabled: enabled,
                            })
                          }
                          onSaveAllocation={(spId, input) =>
                            patchProduct.mutateAsync({ spId, ...input })
                          }
                          onEditOptions={(id, title) => {
                            const row = productItems.find((p) => p.id === id);
                            setOptionsTarget({
                              ids: [id],
                              title,
                              discountPercent: row?.discountPercent ?? null,
                              discountFixedLak: row?.discountFixedLak ?? null,
                            });
                          }}
                          onRemove={(id) => removeProduct.mutate(id)}
                          isSaving={patchProduct.isPending}
                          isRemoving={removeProduct.isPending}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="comments">
                    <TabPanel
                      title="ຄອມເມັນທັງໝົດ"
                      search={
                        <Input
                          placeholder="ຄົ້ນຫາ..."
                          value={commentSearchInput}
                          onChange={(e) => {
                            setCommentSearchInput(e.target.value);
                            debouncedCommentSearch(e.target.value);
                          }}
                          className="max-w-sm"
                        />
                      }
                      action={
                        <button
                          type="button"
                          className={buttonVariants({ size: "sm" })}
                          onClick={() => {
                            setManualProductsPage((page) => ({
                              ...page,
                              offset: 0,
                            }));
                            setManualOrderError(null);
                            setManualOrderTarget({
                              commentIds: [],
                              comments: [],
                            });
                          }}
                        >
                          <Plus className="size-4" />
                          ສ້າງ CF Manual
                        </button>
                      }
                    >
                      <QueryState
                        isLoading={commentsQuery.isLoading}
                        isError={commentsQuery.isError}
                        error={commentsQuery.error}
                        onRetry={() => void commentsQuery.refetch()}
                        isFetching={
                          commentsQuery.isFetching &&
                          !commentsQuery.isFetchingNextPage
                        }
                      >
                        {messengerIdentityPendingTotal > 0 ? (
                          <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-950 text-sm">
                            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                            <p>
                              ມີ {messengerIdentityPendingTotal} ອໍເດີ້ທີ່ລໍຖ້າເຊື່ອມລູກຄ້າ
                              Messenger — ເລືອກ “ເຊື່ອມ Messenger” ໃນລາຍການທີ່ກ່ຽວຂ້ອງ.
                            </p>
                          </div>
                        ) : null}
                        <FeedCommentsTable
                          data={commentRows}
                          isLoading={commentsQuery.isLoading}
                          hasNextPage={Boolean(commentsQuery.hasNextPage)}
                          isFetchingNextPage={commentsQuery.isFetchingNextPage}
                          onLoadMore={() => {
                            if (
                              commentsQuery.hasNextPage &&
                              !commentsQuery.isFetchingNextPage
                            ) {
                              void commentsQuery.fetchNextPage();
                            }
                          }}
                          selectedIds={selectedCommentIds}
                          onSelectionChange={setSelectedCommentIds}
                          onCreateSelected={() => {
                            const commentIds = [...selectedCommentIds];
                            if (commentIds.length === 0) return;
                            setManualProductsPage((page) => ({
                              ...page,
                              offset: 0,
                            }));
                            setManualOrderError(null);
                            setManualOrderTarget({
                              commentIds,
                              comments: commentRows.filter((comment) =>
                                selectedCommentIds.has(comment.id),
                              ),
                            });
                          }}
                          onCreateOrder={(c) => {
                            setManualProductsPage((page) => ({
                              ...page,
                              offset: 0,
                            }));
                            setManualOrderError(null);
                            setManualOrderTarget({
                              commentIds: [c.id],
                              comments: [c],
                            });
                          }}
                          onLinkMessenger={setMessengerLinkTarget}
                        />
                      </QueryState>
                    </TabPanel>
                  </TabsContent>

                  <TabsContent value="guide">
                    <TabPanel title="ວິທີຄອມເມັນເພື່ອສັ່ງຊື້">
                      <FeedCommentOrderGuide />
                    </TabPanel>
                  </TabsContent>
                </Tabs>
              </>
            ) : null}
          </QueryState>
        </div>

        <LinkMessengerProfileDialog
          feedId={feedId}
          comment={messengerLinkTarget}
          open={Boolean(messengerLinkTarget)}
          onOpenChange={(next) => {
            if (!next) setMessengerLinkTarget(null);
          }}
        />

        <ManualOrderDialog
          open={Boolean(manualOrderTarget)}
          onOpenChange={(o) => {
            if (!o) {
              setManualOrderTarget(null);
              setManualOrderError(null);
              setManualProductSearchInput("");
              setManualProductQ("");
              debouncedManualProductSearch("");
            }
          }}
          comments={manualOrderTarget?.comments ?? []}
          commentCount={manualOrderTarget?.commentIds.length ?? 0}
          products={manualProductsQuery.data?.items ?? []}
          productsLoading={manualProductsQuery.isFetching}
          productsTotal={manualProductsQuery.data?.total ?? 0}
          productsOffset={manualProductsPage.offset}
          productsLimit={manualProductsPage.limit}
          productSearch={manualProductSearchInput}
          onProductSearchChange={(value) => {
            setManualProductSearchInput(value);
            debouncedManualProductSearch(value);
          }}
          onProductsPageChange={(offset) =>
            setManualProductsPage((page) => ({ ...page, offset }))
          }
          errorMessage={manualOrderError}
          onClearError={() => setManualOrderError(null)}
          loading={createManualOrder.isPending || createOrders.isPending}
          onSubmit={async (body) => {
            if (!manualOrderTarget) return;
            setManualOrderError(null);
            try {
              if (manualOrderTarget.commentIds.length === 0) {
                await createManualOrder.mutateAsync(body);
                setManualOrderTarget(null);
                setManualProductSearchInput("");
                setManualProductQ("");
                debouncedManualProductSearch("");
                return;
              }

              const result = await createOrders.mutateAsync({
                ...body,
                commentLogIds: manualOrderTarget.commentIds,
              });
              const failedIds = new Set(
                result.failed.map((failure) => failure.commentLogId),
              );
              setSelectedCommentIds(failedIds);
              if (result.failed.length === 0) {
                setManualOrderTarget(null);
                setManualOrderError(null);
                setManualProductSearchInput("");
                setManualProductQ("");
                debouncedManualProductSearch("");
                return;
              }

              const firstFailure = result.failed[0];
              const reason = getManualOrderErrorReason(
                Object.assign(new Error(firstFailure?.code), {
                  code: firstFailure?.code,
                }),
              );
              setManualOrderTarget({
                commentIds: [...failedIds],
                comments: manualOrderTarget.comments.filter((comment) =>
                  failedIds.has(comment.id),
                ),
              });
              setManualOrderError(
                result.failed.length === 1
                  ? reason
                  : `${reason} · ບໍ່ສຳເລັດ ${result.failed.length} ລາຍການ`,
              );
            } catch (err) {
              const reason = getManualOrderErrorReason(err);
              setManualOrderError(reason);
            }
          }}
        />

        <SessionProductOptionsDialog
          feedId={feedId}
          sessionProductIds={optionsTarget?.ids ?? []}
          productTitle={optionsTarget?.title ?? ""}
          discountPercent={optionsTarget?.discountPercent ?? null}
          discountFixedLak={optionsTarget?.discountFixedLak ?? null}
          open={Boolean(optionsTarget)}
          onOpenChange={(next) => {
            if (!next) setOptionsTarget(null);
          }}
          onSaved={() => setSelectedProductIds(new Set())}
        />
        <SpecialWorkDialog
          feedId={feedId}
          orderId={specialWorkOrderId}
          onOpenChange={(open) => {
            if (!open) setSpecialWorkOrderId(null);
          }}
        />
      </Main>
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-muted/20 p-3 text-center sm:px-5 sm:py-6">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-2 font-semibold text-2xl text-primary tracking-tight">
        {value}
      </p>
    </div>
  );
}

function PostMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 space-y-1">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="break-words font-medium text-sm leading-snug">{value}</p>
    </div>
  );
}

function TabPanel({
  title,
  description,
  search,
  action,
  children,
}: {
  title: string;
  description?: string;
  search?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-medium text-lg tracking-tight">{title}</h2>
          {description ? (
            <p className="mt-1 text-muted-foreground text-sm">{description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {search}
          {action}
        </div>
      </div>
      {children}
    </div>
  );
}
